import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../api/gods-garden/axiosConfig';
import { STORAGE_KEYS, CACHE_DURATION } from '../utils/constants';
import { storage, sessionStore, generateCartId, debounce } from '../utils/helpers';

// Create context
const ShopContext = createContext(null);

/**
 * ShopContextProvider - Manages cart and shop state
 */
export const ShopContextProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  // Refs for request management
  const abortControllerRef = useRef(null);

  // Initialize cart
  useEffect(() => {
    const initCart = async () => {
      // Get or create cart ID
      let savedCartId = storage.get(STORAGE_KEYS.CART_ID);
      if (!savedCartId) {
        savedCartId = generateCartId();
        storage.set(STORAGE_KEYS.CART_ID, savedCartId);
      }
      setCartId(savedCartId);

      // Try to load cached cart first and refresh prices before rendering
      const cachedCart = sessionStore.get(`cache_cart_${savedCartId}`);
      if (cachedCart && Date.now() - cachedCart.timestamp < CACHE_DURATION.CART) {
        const cachedItems = await refreshCartPrices(cachedCart.data);
        setCartItems(cachedItems);
        sessionStore.set(`cache_cart_${savedCartId}`, {
          data: cachedItems,
          timestamp: Date.now(),
        });
        setIsLoading(false);
        return;
      }

      // Fetch cart from server
      await fetchCartItems(savedCartId);
    };

    initCart();

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCartPrices = useCallback(async (items) => {
    if (!items || !items.length) return items;

    const refreshed = await Promise.all(
      items.map(async (item) => {
        if (!item.id || !item.size) return item;

        try {
          const response = await api.get(
            `/get-product-detail/?product_pk=${item.id}&size=${encodeURIComponent(item.size)}`
          );
          const productData = response.data?.data;
          if (!productData) return item;

          return {
            ...item,
            offer_price: productData.offer_price ?? item.offer_price,
            mrp: productData.mrp ?? item.mrp,
          };
        } catch (err) {
          return item;
        }
      })
    );

    return refreshed;
  }, []);

  // Fetch cart items from server
  const fetchCartItems = useCallback(async (id = cartId, forceRefresh = false) => {
    if (!id) return;

    // Check cache if not forcing refresh
    if (!forceRefresh) {
      const cachedCart = sessionStore.get(`cache_cart_${id}`);
      if (cachedCart && Date.now() - cachedCart.timestamp < CACHE_DURATION.CART) {
        const cachedItems = await refreshCartPrices(cachedCart.data);
        setCartItems(cachedItems);
        sessionStore.set(`cache_cart_${id}`, {
          data: cachedItems,
          timestamp: Date.now(),
        });
        setIsLoading(false);
        return;
      }
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get(`/get-cart-items/?cart_id=${id}`, {
        signal: abortControllerRef.current.signal,
      });

      let items = response.data?.data || [];
      items = await refreshCartPrices(items);

      setCartItems(items);
      // Cache the result
      sessionStore.set(`cache_cart_${id}`, {
        data: items,
        timestamp: Date.now(),
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching cart:', err);
        setError('Failed to load cart');
      }
    } finally {
      setIsLoading(false);
    }
  }, [cartId, refreshCartPrices]);

  // Debounced fetch
  const debouncedFetch = useMemo(
    () => debounce(() => fetchCartItems(cartId, true), 300),
    [cartId, fetchCartItems]
  );

  // Add item to cart
  const addToCart = useCallback(
    async (productId, size, quantity = 1, offerPrice, mrp, isDirectBuy = false) => {
      if (!cartId) return { success: false, error: 'Cart not initialized' };

      setIsUpdating(true);
      setError(null);

      // Optimistic update
      const existingIndex = cartItems.findIndex(
        (item) => item.id === productId && item.size === size
      );

      const previousItems = [...cartItems];

      // Backend sets the quantity for a cart line rather than incrementing it,
      // so the request must always carry the cumulative total, not just the delta.
      const totalQuantity =
        existingIndex >= 0 ? cartItems[existingIndex].quantity + quantity : quantity;

      if (existingIndex >= 0) {
        // Update existing item
        setCartItems((prev) => {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: totalQuantity,
          };
          return updated;
        });
      } else {
        // Add new item (placeholder)
        setCartItems((prev) => [
          ...prev,
          {
            id: productId,
            size,
            quantity,
            offer_price: offerPrice,
            mrp,
            _pending: true,
          },
        ]);
      }

      try {
        const response = await api.post('/cart-items/', {
          cart_id: cartId,
          product_pk: productId,
          size,
          quantity: totalQuantity,
          offer_price: offerPrice,
          mrp,
          is_direct_buy: isDirectBuy,
        });

        // Refresh cart to get accurate data
        await fetchCartItems(cartId, true);

        return { success: true, data: response.data };
      } catch (err) {
        // Rollback optimistic update
        setCartItems(previousItems);
        const errorMessage = err.response?.data?.error || 'Failed to add item to cart';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsUpdating(false);
      }
    },
    [cartId, cartItems, fetchCartItems]
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    async (productId, size, offerPrice, mrp, decrementOnly = false) => {
      if (!cartId) return { success: false, error: 'Cart not initialized' };

      setIsUpdating(true);
      setError(null);

      // Find item
      const itemIndex = cartItems.findIndex(
        (item) => item.id === productId && item.size === size
      );

      if (itemIndex === -1) {
        setIsUpdating(false);
        return { success: false, error: 'Item not found in cart' };
      }

      const previousItems = [...cartItems];

      // Optimistic update
      if (decrementOnly && cartItems[itemIndex].quantity > 1) {
        setCartItems((prev) => {
          const updated = [...prev];
          updated[itemIndex] = {
            ...updated[itemIndex],
            quantity: updated[itemIndex].quantity - 1,
          };
          return updated;
        });
      } else {
        setCartItems((prev) => prev.filter((_, i) => i !== itemIndex));
      }

      try {
        await api.post('/remove-cart-items/', {
          cart_id: cartId,
          product_pk: productId,
          size,
          offer_price: offerPrice,
          mrp,
        });

        // Refresh cart
        await fetchCartItems(cartId, true);

        return { success: true };
      } catch (err) {
        // Rollback
        setCartItems(previousItems);
        const errorMessage = err.response?.data?.error || 'Failed to remove item';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsUpdating(false);
      }
    },
    [cartId, cartItems, fetchCartItems]
  );

  // Update item quantity
  const updateQuantity = useCallback(
    async (productId, size, newQuantity, offerPrice, mrp) => {
      if (newQuantity < 1) {
        return removeFromCart(productId, size, offerPrice, mrp);
      }

      const item = cartItems.find((i) => i.id === productId && i.size === size);
      if (!item) return { success: false, error: 'Item not found' };

      const diff = newQuantity - item.quantity;
      if (diff === 0) {
        return { success: true }; // No change needed
      }

      setIsUpdating(true);
      const previousItems = [...cartItems];

      // Optimistic update
      setCartItems((prev) =>
        prev.map((i) =>
          i.id === productId && i.size === size ? { ...i, quantity: newQuantity } : i
        )
      );

      try {
        if (diff > 0) {
          // Increase quantity - backend sets the absolute quantity, so send the new total
          await api.post('/cart-items/', {
            cart_id: cartId,
            product_pk: productId,
            size,
            quantity: newQuantity,
            offer_price: offerPrice,
            mrp,
          });
        } else {
          // Decrease quantity - remove completely then add back with new quantity
          await api.post('/remove-cart-items/', {
            cart_id: cartId,
            product_pk: productId,
            size,
            offer_price: offerPrice,
            mrp,
          });

          if (newQuantity > 0) {
            await api.post('/cart-items/', {
              cart_id: cartId,
              product_pk: productId,
              size,
              quantity: newQuantity,
              offer_price: offerPrice,
              mrp,
            });
          }
        }

        // Update cache with new data
        const updatedItems = cartItems.map((i) =>
          i.id === productId && i.size === size ? { ...i, quantity: newQuantity } : i
        );
        sessionStore.set(`cache_cart_${cartId}`, {
          data: updatedItems,
          timestamp: Date.now(),
        });

        return { success: true };
      } catch (err) {
        // Rollback optimistic update
        setCartItems(previousItems);
        return { success: false, error: 'Failed to update quantity' };
      } finally {
        setIsUpdating(false);
      }
    },
    [cartId, cartItems, removeFromCart]
  );

  // Clear cart
  const clearCart = useCallback(async () => {
    setCartItems([]);
    sessionStore.remove(`cache_cart_${cartId}`);

    // Generate new cart ID
    const newCartId = generateCartId();
    storage.set(STORAGE_KEYS.CART_ID, newCartId);
    setCartId(newCartId);
  }, [cartId]);

  // Validate promo code
  const validatePromoCode = useCallback(async (promoCode, amount, itemCount) => {
    try {
      const response = await api.post('/validate-promo-code/', {
        promo_code: promoCode,
        amount: amount.toString(),
        no_of_products: itemCount,
      });

      const discountAmount = Number(
          response.data.amount ??
          0
      );
      const discountPercentage = Number(
        response.data.discount_percent ??
          response.data.discount_percentage ??
          response.data.percentage ??
          (amount > 0 && discountAmount > 0 ? (discountAmount / amount) * 100 : 0)
      );

      return {
        success: true,
        discount: discountAmount,
        discountAmount,
        discountPercentage,
        data: response.data,
      };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.error || 'Invalid promo code',
      };
    }
  }, []);

  // Calculate totals
  const cartTotals = useMemo(() => {
    const subtotal = cartItems.reduce(
      (sum, item) => sum + (item.offer_price || 0) * (item.quantity || 0),
      0
    );

    const originalTotal = cartItems.reduce(
      (sum, item) => sum + (item.mrp || item.offer_price || 0) * (item.quantity || 0),
      0
    );

    const savings = originalTotal - subtotal;
    const itemCount = cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

    return {
      subtotal,
      originalTotal,
      savings,
      itemCount,
    };
  }, [cartItems]);

  // Context value
  const value = useMemo(
    () => ({
      // State
      cartItems,
      cartId,
      isLoading,
      isUpdating,
      error,

      // Computed
      cartCount: cartTotals.itemCount,
      cartTotal: cartTotals.subtotal,
      cartTotals,

      // Methods
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      fetchCartItems: debouncedFetch,
      validatePromoCode,

      // Clear error
      clearError: () => setError(null),
    }),
    [
      cartItems,
      cartId,
      isLoading,
      isUpdating,
      error,
      cartTotals,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      debouncedFetch,
      validatePromoCode,
    ]
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

/**
 * useShop hook
 * @returns {object} Shop context value
 */
export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopContextProvider');
  }
  return context;
};

export default ShopContext;
